/* Notification Center — demo data.
 *
 * AI-only design module (no ALPACA NavigationView, no backend yet), exactly like
 * Performance Analytics.  The data here mirrors the standalone notification-center
 * prototype 1:1 so the React module shows the same rules / categories / recipients
 * / templates / deliveries.  When the real API lands this file is replaced by a
 * reader hook — the components consume these typed shapes either way. */

export type Severity = 'critical' | 'warning' | 'info';
export type Purpose = 'internal' | 'external' | 'violation' | 'reminder' | 'system' | 'escalation';
export type DeliveryStatus = 'Delivered' | 'Read' | 'Sent' | 'Failed' | 'Bounced';

export const PURPOSE_LABEL: Record<Purpose, string> = {
  internal: 'Internal Alert',
  external: 'External Communication',
  violation: 'Compliance Violation',
  reminder: 'Workflow Reminder',
  system: 'System Failure',
  escalation: 'Escalation',
};

export interface NcRule {
  name: string;
  code: string;
  purpose: Purpose;
  /** Alert severity — omitted for outbound communications. */
  severity?: Severity;
  /** Outbound communication (to an external party) rather than an internal alert. */
  outbound?: boolean;
  /** Per-target monitoring count, e.g. "5 targets" — omitted when none. */
  targets?: string;
  recipients: string;
  /** Whether the recipient is an external party (renders differently). */
  externalRecipient?: boolean;
  ack?: boolean;
  enabled: boolean;
}

export interface NcCategory {
  code: number;
  name: string;
  icon: string;
  order: number;
  active: boolean;
  rules: NcRule[];
}

export interface NcRecipientGroup {
  name: string;
  members: string;
  channels: string[];
  usedBy: number;
}

export interface NcTemplate {
  name: string;
  category: string;
  subject: string;
  body?: string;
  active: boolean;
}

export interface NcDelivery {
  time: string;
  rule: string;
  channel: string;
  recipient: string;
  status: DeliveryStatus;
  detail: string;
}

export const NC_CATEGORIES: NcCategory[] = [
  {
    code: 1, name: 'Result Exceedance', icon: 'ti-droplet-exclamation', order: 10, active: true,
    rules: [
      { name: 'Permit Limit Exceedance', code: 'EXC-PERMIT-01', purpose: 'violation', severity: 'critical', targets: '5 targets', recipients: 'Lab Director, QA Manager', ack: true, enabled: true },
      { name: 'DW Micro Positive — Confirmed Repeat', code: 'EXC-MICRO-DW-02', purpose: 'violation', severity: 'critical', targets: '2 targets', recipients: 'Lab Director, QA Manager', ack: true, enabled: true },
    ],
  },
  {
    code: 2, name: 'Sample Receipt', icon: 'ti-package-import', order: 20, active: true,
    rules: [
      { name: 'Cooler / Sample Temperature Out of Range', code: 'RCPT-TEMP-001', purpose: 'violation', severity: 'critical', targets: '1 target', recipients: 'Sample Receiving, QA Manager', enabled: true },
      { name: 'Preservation Failure (pH / preservative)', code: 'RCPT-PRES-002', purpose: 'violation', severity: 'critical', targets: '2 targets', recipients: 'Sample Receiving, QA Manager', enabled: true },
      { name: 'Insufficient Sample Volume', code: 'RCPT-VOL-003', purpose: 'reminder', severity: 'warning', recipients: 'Sample Receiving, Project Manager', enabled: true },
      { name: 'Broken / Leaking Container', code: 'RCPT-CONT-004', purpose: 'reminder', severity: 'warning', recipients: 'Sample Receiving', enabled: true },
      { name: 'COC Discrepancy', code: 'RCPT-COC-005', purpose: 'reminder', severity: 'warning', recipients: 'Sample Receiving, Project Manager', enabled: true },
      { name: 'Field QC Missing (trip / field blank)', code: 'RCPT-FQC-006', purpose: 'reminder', severity: 'info', recipients: 'Project Manager', enabled: false },
      { name: 'Received Past Hold Time', code: 'RCPT-HT-007', purpose: 'violation', severity: 'critical', recipients: 'QA Manager, Project Manager', ack: true, enabled: true },
    ],
  },
  {
    code: 3, name: 'Compliance & TAT', icon: 'ti-clipboard-check', order: 30, active: true,
    rules: [
      { name: 'Holding Time Violation', code: 'CMPL-HT-03', purpose: 'violation', severity: 'critical', targets: '6 targets', recipients: 'Analyst, QA Manager', ack: true, enabled: true },
      { name: 'Extraction / Prep Hold Time Approaching', code: 'TAT-XHOLD-010', purpose: 'reminder', severity: 'warning', targets: '4 targets', recipients: 'Analyst', enabled: true },
      { name: 'Analysis Hold Time Approaching', code: 'TAT-AHOLD-011', purpose: 'reminder', severity: 'warning', targets: '6 targets', recipients: 'Analyst, Lab Manager', enabled: true },
      { name: 'Sample Due Warning', code: 'TAT-DUE-012', purpose: 'reminder', severity: 'warning', targets: '1 target', recipients: 'Lab Manager', enabled: true },
      { name: 'Sample Past Due', code: 'TAT-PASTDUE-013', purpose: 'escalation', severity: 'critical', targets: '1 target', recipients: 'Lab Manager, Account Owner', ack: true, enabled: true },
      { name: 'Calibration Expiring (7 days)', code: 'CMPL-CAL-WARN', purpose: 'reminder', severity: 'warning', targets: '1 target', recipients: 'QA Manager', enabled: true },
      { name: 'Accreditation / Certification Expiring', code: 'CERT-EXP-014', purpose: 'violation', severity: 'warning', recipients: 'QA Manager', enabled: true },
      { name: 'PT / PE Sample Due', code: 'CERT-PT-015', purpose: 'violation', severity: 'warning', recipients: 'QA Manager', enabled: true },
      { name: 'PT Failure', code: 'CERT-PTFAIL-016', purpose: 'violation', severity: 'critical', recipients: 'QA Manager, Lab Director', ack: true, enabled: true },
      { name: 'DMR / Regulatory Report Deadline', code: 'CMPL-DMR-017', purpose: 'violation', severity: 'warning', recipients: 'Lab Manager, Compliance', enabled: true },
      { name: 'CAPA / NCR Overdue', code: 'CMPL-CAPA-018', purpose: 'violation', severity: 'warning', recipients: 'QA Manager', enabled: false },
    ],
  },
  {
    code: 4, name: 'QC & Workflow', icon: 'ti-flask', order: 40, active: true,
    rules: [
      { name: 'QC Batch Failure (CCV / LCS)', code: 'QC-FAIL-02', purpose: 'reminder', severity: 'critical', targets: '3 targets', recipients: 'Analyst, QA Manager', enabled: true },
      { name: 'Method Blank Contamination', code: 'QC-MB-020', purpose: 'reminder', severity: 'warning', targets: '1 target', recipients: 'Analyst, QA Manager', enabled: true },
      { name: 'Surrogate Recovery Out of Limits', code: 'QC-SUR-021', purpose: 'reminder', severity: 'warning', targets: '2 targets', recipients: 'Analyst', enabled: true },
      { name: 'LCS / LCSD Out of Control', code: 'QC-LCS-022', purpose: 'reminder', severity: 'critical', targets: '2 targets', recipients: 'Analyst, QA Manager', enabled: true },
      { name: 'MS / MSD Recovery or RPD Failure', code: 'QC-MSD-023', purpose: 'reminder', severity: 'warning', targets: '2 targets', recipients: 'Analyst', enabled: true },
      { name: 'Control Chart Out of Control', code: 'QC-CHART-024', purpose: 'reminder', severity: 'warning', recipients: 'QA Manager', enabled: false },
      { name: 'Result Over Calibration Range (dilution)', code: 'QC-RANGE-025', purpose: 'reminder', severity: 'warning', recipients: 'Analyst', enabled: true },
      { name: 'Standard / Reagent Expiration', code: 'QC-REAG-026', purpose: 'reminder', severity: 'warning', recipients: 'Analyst, QA Manager', enabled: true },
      { name: 'Sample Stuck in Status', code: 'QC-STUCK-01', purpose: 'reminder', severity: 'warning', targets: '1 target', recipients: 'Lab Manager', enabled: true },
      { name: 'Secondary Review / Approval Pending', code: 'QC-REVIEW-027', purpose: 'reminder', severity: 'info', recipients: 'Reviewer', enabled: false },
    ],
  },
  {
    code: 5, name: 'Client & Sales', icon: 'ti-users', order: 50, active: true,
    rules: [
      { name: 'Sample Receipt Confirmation → Client', code: 'CLNT-RCPT-030', purpose: 'external', outbound: true, recipients: 'Client contact (external)', externalRecipient: true, enabled: true },
      { name: 'Report Ready / Delivered → Client', code: 'CLNT-RPT-031', purpose: 'external', outbound: true, recipients: 'Client contact (external)', externalRecipient: true, enabled: true },
      { name: 'Client At-Risk Cadence Gap', code: 'CLNT-CADENCE-01', purpose: 'internal', severity: 'warning', targets: '1 target', recipients: 'Assigned Salesperson (dynamic)', enabled: true },
      { name: 'Salesperson Scorecard Below Target', code: 'CLNT-SCORE-04', purpose: 'internal', severity: 'info', recipients: 'Assigned Salesperson', enabled: false },
      { name: 'Invoice Overdue', code: 'CLNT-INV-032', purpose: 'internal', severity: 'warning', recipients: 'Account Owner, Billing', enabled: true },
    ],
  },
  {
    code: 6, name: 'Subcontract & Logistics', icon: 'ti-truck-delivery', order: 60, active: true,
    rules: [
      { name: 'Subout Order → Subcontract Lab', code: 'SUB-ORDER-040', purpose: 'external', outbound: true, recipients: 'Subcontractor lab (external)', externalRecipient: true, enabled: true },
      { name: 'Subout Result Overdue', code: 'SUB-OVERDUE-041', purpose: 'reminder', severity: 'warning', targets: '1 target', recipients: 'Project Manager, Lab Manager', enabled: true },
      { name: 'Subout Result Received (needs import)', code: 'SUB-RECV-042', purpose: 'reminder', severity: 'info', recipients: 'Project Manager', enabled: true },
      { name: 'Sampling / Courier Pickup Missed', code: 'SUB-PICKUP-043', purpose: 'reminder', severity: 'warning', recipients: 'Project Manager', enabled: false },
    ],
  },
  {
    code: 7, name: 'System & Data', icon: 'ti-server-2', order: 70, active: true,
    rules: [
      { name: 'Report Delivery Failure', code: 'SYS-RPTFAIL-050', purpose: 'system', severity: 'critical', recipients: 'Report Owner, IT', enabled: true },
      { name: 'Email Failure (SMTP bounce)', code: 'SYS-EMAIL-051', purpose: 'system', severity: 'critical', recipients: 'Sender, IT', enabled: true },
      { name: 'EDD Generation / Format Failure', code: 'SYS-EDD-052', purpose: 'system', severity: 'warning', recipients: 'Data Team, Project Manager', enabled: true },
      { name: 'SDMS / Parser Import Failure', code: 'SYS-PARSE-01', purpose: 'system', severity: 'info', recipients: 'Data Team', enabled: true },
      { name: 'Instrument Data Import Failure', code: 'SYS-IMPORT-053', purpose: 'system', severity: 'warning', recipients: 'Data Team', enabled: true },
      { name: 'Client Portal Upload Failure', code: 'SYS-PORTAL-054', purpose: 'system', severity: 'warning', recipients: 'IT, Project Manager', enabled: true },
      { name: 'Backup / Scheduled Job Failure', code: 'SYS-JOB-055', purpose: 'system', severity: 'warning', recipients: 'IT', enabled: false },
    ],
  },
  {
    code: 8, name: 'Capacity & Trend', icon: 'ti-trending-up', order: 80, active: false,
    rules: [
      { name: 'Workload Imbalance Across Analysts', code: 'CAP-WORKLOAD-01', purpose: 'internal', severity: 'info', targets: '3 targets', recipients: 'Lab Manager', enabled: false },
      { name: 'Backlog Threshold Exceeded', code: 'CAP-BACKLOG-060', purpose: 'internal', severity: 'warning', recipients: 'Lab Manager', enabled: true },
      { name: 'Instrument Out of Service / Down', code: 'CAP-INSTR-061', purpose: 'internal', severity: 'warning', recipients: 'Lab Manager, Maintenance', enabled: true },
    ],
  },
];

export const NC_RECIPIENT_GROUPS: NcRecipientGroup[] = [
  { name: 'Lab Leadership', members: 'Lab Director, QA Manager', channels: ['IN-APP', 'EMAIL'], usedBy: 9 },
  { name: 'DW Compliance Team', members: 'Lab Director, QA Manager, Compliance', channels: ['IN-APP', 'EMAIL'], usedBy: 4 },
  { name: 'Sample Receiving', members: 'Sample Receiving (role)', channels: ['IN-APP', 'EMAIL'], usedBy: 7 },
  { name: 'Account Owners (dynamic)', members: 'Client.AssignedSalesperson', channels: ['IN-APP', 'EMAIL'], usedBy: 5 },
  { name: 'IT / Data Team', members: 'IT, Data Team', channels: ['EMAIL'], usedBy: 6 },
];

export const NC_TEMPLATES: NcTemplate[] = [
  { name: 'Critical Exceedance', category: 'Result Exceedance', subject: '[ALPACA] Critical: {{RuleName}} — {{SampleId}}', body: 'A critical result was recorded for {{Client}}.\n\nSample: {{SampleId}}\nAnalyte: {{Analyte}}\nResult: {{Result}} (limit {{Limit}})\nFired: {{FiredAt}}', active: true },
  { name: 'Sample Receipt Confirmation', category: 'Client & Sales', subject: 'Your samples were received — {{JobId}}', body: 'Hello {{Client}},\n\nWe have received your samples under {{JobId}}.\nContainers: {{ContainerCount}}\nExpected completion: {{DueDate}}', active: true },
  { name: 'Subout Order', category: 'Subcontract & Logistics', subject: 'Subcontract order {{OrderId}} — {{TestList}}', body: 'Please run the following on the attached samples:\n{{TestList}}\n\nReturn by: {{DueDate}}', active: true },
  { name: 'TAT Warning Digest', category: 'Compliance & TAT', subject: 'Samples due soon — {{Count}} pending', body: '{{Count}} samples are approaching their due window.\nOldest: {{OldestSample}}', active: true },
  { name: 'QC Failure', category: 'QC & Workflow', subject: 'QC failure: {{Batch}} — {{Analyte}}', body: 'QC failed on batch {{Batch}}.\nAnalyte: {{Analyte}}\nRecovery: {{Recovery}}', active: false },
  { name: 'Sample Condition Issue', category: 'Sample Receipt', subject: '[ALPACA] Sample condition issue — {{SampleId}}', body: 'A receipt condition issue was flagged for {{Client}}.\nSample: {{SampleId}}\nIssue: {{Issue}}', active: true },
  { name: 'System Alert', category: 'System & Data', subject: '[ALPACA] System alert — {{RuleName}}', body: 'A system event needs attention.\nRule: {{RuleName}}\nDetail: {{Detail}}', active: true },
  { name: 'Capacity Alert', category: 'Capacity & Trend', subject: '[ALPACA] Capacity — {{RuleName}}', body: 'A capacity / trend threshold was crossed.\nRule: {{RuleName}}\nDetail: {{Detail}}', active: true },
];

export const NC_DELIVERIES: NcDelivery[] = [
  { time: '06/20/2026 09:33', rule: 'Permit Limit Exceedance', channel: 'EMAIL', recipient: 'lab.director@agtlabs.com', status: 'Delivered', detail: '—' },
  { time: '06/20/2026 09:33', rule: 'Permit Limit Exceedance', channel: 'IN-APP', recipient: 'QA Manager', status: 'Read', detail: 'Read 09:41' },
  { time: '06/20/2026 08:40', rule: 'Subout Order → Eurofins', channel: 'EMAIL', recipient: 'orders@eurofins.com', status: 'Sent', detail: '—' },
  { time: '06/20/2026 07:55', rule: 'Report Delivery → West County', channel: 'EMAIL', recipient: 'client portal', status: 'Failed', detail: 'Portal upload timeout' },
  { time: '06/20/2026 07:10', rule: 'Sample Receipt → River Ranch', channel: 'EMAIL', recipient: 'ops@riverranch.com', status: 'Bounced', detail: '550 mailbox full' },
  { time: '06/19/2026 16:42', rule: 'Calibration Expiring', channel: 'EMAIL', recipient: 'qa@agtlabs.com', status: 'Delivered', detail: '—' },
];

/** Derived summary counts (kept in one place so the Overview KPIs and the
 *  sidebar counts can never drift from the rule data above). */
export const NC_SUMMARY = (() => {
  const allRules = NC_CATEGORIES.flatMap((c) => c.rules);
  return {
    totalRules: allRules.length,
    activeRules: allRules.filter((r) => r.enabled).length,
    disabledRules: allRules.filter((r) => !r.enabled).length,
    requireAck: allRules.filter((r) => r.ack).length,
    outbound: allRules.filter((r) => r.outbound).length,
    categories: NC_CATEGORIES.length,
    activeCategories: NC_CATEGORIES.filter((c) => c.active).length,
    recipientGroups: NC_RECIPIENT_GROUPS.length,
    templates: NC_TEMPLATES.length,
    deliveries24h: 524,
    deliveryFailures: 6,
  };
})();
